import { z } from "zod";
import ProposalSchema, { type ProposalJSON } from "work/proposals/_schemas/proposal-schema";

export const ProposalNegotiationPartyZ = z.enum(["Client", "Vendor"]);
export type ProposalNegotiationParty = z.infer<typeof ProposalNegotiationPartyZ>;

export const ProposalActorRoleZ = z.enum(["TeamLeader", "Reviewer", "Participant", "System", "LegacyUnknown"]);
export type ProposalActorRole = z.infer<typeof ProposalActorRoleZ>;

export const ProposalContentOperationKindZ = z.enum([
  "FieldSet",
  "CollectionItemAdded",
  "CollectionItemUpdated",
  "CollectionItemRemoved",
  "ResolutionDecision",
]);
export type ProposalContentOperationKind = z.infer<typeof ProposalContentOperationKindZ>;

export const ProposalResolutionDispositionZ = z.enum([
  "Unresolved",
  "AcceptIncoming",
  "KeepPrevious",
  "ProposeAlternative",
]);
export type ProposalResolutionDisposition = z.infer<typeof ProposalResolutionDispositionZ>;

export const ProposalResolutionOriginZ = z.enum(["Counterparty", "Reviewer"]);
export type ProposalResolutionOrigin = z.infer<typeof ProposalResolutionOriginZ>;

export const ProposalNegotiationEventKindZ = z.enum([
  "NegotiationStarted",
  "FieldSet",
  "CollectionItemAdded",
  "CollectionItemUpdated",
  "CollectionItemRemoved",
  "ReviewerAssigned",
  "ReviewerRemoved",
  "ReviewerAccessChanged",
  "ReviewerMessageChanged",
  "ReviewCompleted",
  "ReviewRequested",
  "ResolutionDecisionRecorded",
  "ResolutionTargetPublished",
  "SubmissionCreated",
  "ConflictDetected",
  "ConflictResolved",
  "CommentAdded",
  "CommentThreadLinked",
  "NegotiationCanceled",
  "ProposalHired",
  "ProposalAccepted",
  "ProposalRejected",
  "RevisionRequested",
  "ParticipantAssociated",
  "ParticipantRemoved",
  "LegacyImported",
]);
export type ProposalNegotiationEventKind = z.infer<typeof ProposalNegotiationEventKindZ>;

export const ProposalCommentAudienceZ = z.enum(["InternalToParty", "PublishedToCounterparty"]);
export type ProposalCommentAudience = z.infer<typeof ProposalCommentAudienceZ>;

const ProposalContentOperationBaseZ = z.object({
  operationId: z.uuid(),
  path: z.string().startsWith("/"),
  conflictKey: z.string().min(1),
});

export const ProposalContentOperationZ = z.discriminatedUnion("kind", [
  ProposalContentOperationBaseZ.extend({
    kind: z.literal("FieldSet"),
    expectedValue: z.json().optional(),
    value: z.json().optional(),
  }),
  ProposalContentOperationBaseZ.extend({
    kind: z.literal("CollectionItemAdded"),
    itemId: z.string().min(1),
    value: z.json(),
  }),
  ProposalContentOperationBaseZ.extend({
    kind: z.literal("CollectionItemUpdated"),
    itemId: z.string().min(1),
    expectedValue: z.json().optional(),
    value: z.json(),
  }),
  ProposalContentOperationBaseZ.extend({
    kind: z.literal("CollectionItemRemoved"),
    itemId: z.string().min(1),
    expectedValue: z.json().optional(),
  }),
  ProposalContentOperationBaseZ.extend({
    kind: z.literal("ResolutionDecision"),
    itemId: z.string().min(1).optional(),
    expectedValue: ProposalResolutionDispositionZ,
    value: ProposalResolutionDispositionZ,
    targetValue: z.json().optional(),
  }),
]);
export type ProposalContentOperation = z.infer<typeof ProposalContentOperationZ>;
export type ProposalResolutionDecisionOperation = Extract<
  ProposalContentOperation,
  { kind: "ResolutionDecision" }
>;
/**
 * The operation kind a *plain scalar* revert (`buildProposalScalarFieldRevertOperation`) is allowed to
 * produce — structurally excludes `CollectionItemAdded`/`CollectionItemUpdated`/`CollectionItemRemoved`/
 * `ResolutionDecision`, so a future change to that builder that tried to return one of those instead
 * (or a call site that tried to feed one of those into `proposalScalarFieldRevertValue`) fails to
 * compile rather than silently promoting a collection edit into a whole-field overwrite. See
 * proposal-collection-item-revert.ts and ROADMAP.md's "a revert is never a raw FieldSet" — read the
 * other direction, a raw FieldSet is never a collection-item revert either
 * (`ProposalCollectionItemRevertOperation` below).
 */
export type ProposalFieldSetOperation = Extract<ProposalContentOperation, { kind: "FieldSet" }>;
/**
 * The operation kinds a *collection-item* revert (`buildProposalCollectionItemRevertOperation`) is
 * allowed to produce — structurally excludes `FieldSet`. This is the type-level form of ROADMAP.md's
 * standing constraint "a revert is never a raw FieldSet — it silently promotes the field to
 * ProposeAlternative": with this as the builder's return type and `proposalCollectionItemRevertValue`'s
 * parameter type, neither side of that mistake can be written and still typecheck.
 */
export type ProposalCollectionItemRevertOperation = Extract<
  ProposalContentOperation,
  { kind: "CollectionItemAdded" } | { kind: "CollectionItemUpdated" }
>;

export const ProposalConflictZ = z.object({
  conflictId: z.uuid(),
  conflictKey: z.string().min(1),
  path: z.string().startsWith("/"),
  expectedValue: z.json().optional(),
  currentValue: z.json().optional(),
  attemptedValue: z.json().optional(),
  currentActorUserId: z.uuid().nullish(),
  currentSequence: z.number().int().nonnegative(),
});
export type ProposalConflict = z.infer<typeof ProposalConflictZ>;

// S2 (ROADMAP.md, "a silent drop is a bug; make it loud"): the semantic merge never rejects a
// save for a content conflict -- a losing operation simply does not apply, last-write-wins. This
// is the receipt: one entry per operation the command submitted, naming whether it took effect
// and, when it did not, why. Additive and optional on the wire -- an older client (or an envelope
// from a command that never runs the merge, e.g. approve/reject) parses fine with an empty array,
// which reads as "everything applied", the historical behaviour.
export const ProposalOperationReceiptReasonZ = z.enum(["Conflict", "Unresolvable", "SupersededTarget"]);
export type ProposalOperationReceiptReason = z.infer<typeof ProposalOperationReceiptReasonZ>;

export const ProposalOperationReceiptZ = z.object({
  operationId: z.uuid(),
  conflictKey: z.string().min(1),
  path: z.string().startsWith("/"),
  applied: z.boolean(),
  // Present only when `applied` is false.
  reason: ProposalOperationReceiptReasonZ.nullish(),
});
export type ProposalOperationReceipt = z.infer<typeof ProposalOperationReceiptZ>;

export const ProposalActivityZ = z.object({
  sequence: z.number().int().positive(),
  eventId: z.uuid(),
  commandId: z.uuid(),
  party: ProposalNegotiationPartyZ.nullish(),
  actorUserId: z.uuid().nullish(),
  actorLegalEntityId: z.uuid().nullish(),
  actorRole: ProposalActorRoleZ,
  kind: ProposalNegotiationEventKindZ,
  path: z.string().startsWith("/").nullish(),
  itemId: z.string().min(1).nullish(),
  messageId: z.uuid().nullish(),
  commentAudience: ProposalCommentAudienceZ.nullish(),
  conflictKey: z.string().nullish(),
  disposition: ProposalResolutionDispositionZ.nullish(),
  createdDate: z.iso.datetime({ offset: true }),
});
export type ProposalActivity = z.infer<typeof ProposalActivityZ>;

export const ProposalChangeLineZ = z.object({
  conflictKey: z.string().min(1),
  path: z.string().startsWith("/"),
  itemId: z.string().min(1).nullish(),
  lastChangedByParty: ProposalNegotiationPartyZ,
  lastChangedSequence: z.number().int().positive(),
  // Optional during the rolling API deployment. A missing flag must never be read as
  // "unpublished": that would offer the leader a tranche of changes the counterparty has
  // already seen. `proposalPublishableChanges` treats the silence as "already published".
  isPublished: z.boolean().optional(),
  // The value the counterparty can still see here — what this change would replace. The server sets it
  // only on the viewing party's own unpublished lines (the submit dialog's own list), so it is absent
  // on every other line by design, and absent altogether from an older API deploy: a change with no
  // previous value simply describes itself by its new value, exactly as it did before.
  previousValueExists: z.boolean().optional(),
  previousValue: z.json().optional(),
});
export type ProposalChangeLine = z.infer<typeof ProposalChangeLineZ>;

export const ProposalResolutionStateZ = z
  .object({
    submissionId: z.uuid(),
    path: z.string().startsWith("/"),
    conflictKey: z.string().min(1),
    itemId: z.string().nullable(),
    incomingParty: ProposalNegotiationPartyZ,
    incomingSequence: z.number().int().positive(),
    // Additive (Wave 2b). Absent on historical envelopes → treated as "Counterparty".
    // A "Reviewer" target is an intra-party blue-line whose `incomingParty` equals the
    // viewing party, so origin (not party) is what distinguishes it from an own-value.
    origin: ProposalResolutionOriginZ.optional(),
    disposition: ProposalResolutionDispositionZ,
    baselineValueExists: z.boolean(),
    baselineValue: z.json().optional(),
    targetValueExists: z.boolean(),
    targetValue: z.json().optional(),
    // Author of the incoming candidate ("theirs") value: the reviewer for a Reviewer
    // target, the counterparty leader (when known) for a Counterparty target. Distinct
    // from actor* below, which is whoever recorded the resolution decision.
    incomingActorUserId: z.uuid().nullish(),
    incomingActorLegalEntityId: z.uuid().nullish(),
    incomingActorRole: ProposalActorRoleZ.nullish(),
    actorUserId: z.uuid().nullable(),
    actorLegalEntityId: z.uuid().nullable(),
    actorRole: ProposalActorRoleZ.nullable(),
    decisionSequence: z.number().int().positive().nullable(),
    decisionDate: z.iso.datetime({ offset: true }).nullable(),
  })
  .superRefine((state, context) => {
    const hasBaselineValue = Object.prototype.hasOwnProperty.call(state, "baselineValue");
    if (state.baselineValueExists !== hasBaselineValue) {
      context.addIssue({
        code: "custom",
        path: ["baselineValue"],
        message: "baselineValue must be present exactly when baselineValueExists is true.",
      });
    }
    const hasTargetValue = Object.prototype.hasOwnProperty.call(state, "targetValue");
    if (state.targetValueExists !== hasTargetValue) {
      context.addIssue({
        code: "custom",
        path: ["targetValue"],
        message: "targetValue must be present exactly when targetValueExists is true.",
      });
    }
  });
export type ProposalResolutionState = z.infer<typeof ProposalResolutionStateZ>;

export const ProposalReviewerApprovalZ = z.object({
  reviewerUserId: z.uuid(),
  reviewerLegalEntityId: z.uuid(),
  party: ProposalNegotiationPartyZ,
  approvedSubmissionId: z.uuid().nullable(),
  approvedContentVersion: z.number().int().nonnegative().nullish(),
  approvedDate: z.iso.datetime({ offset: true }).nullish(),
  isCurrent: z.boolean(),
});
export type ProposalReviewerApproval = z.infer<typeof ProposalReviewerApprovalZ>;

export const ProposalReviewerChangeZ = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("Assign"),
    reviewerUserId: z.uuid(),
    reviewerLegalEntityId: z.uuid(),
    canEdit: z.boolean(),
    customMessage: z.string().nullish(),
  }),
  z.object({
    kind: z.literal("Remove"),
    reviewerUserId: z.uuid(),
    reviewerLegalEntityId: z.uuid(),
    expectedCanEdit: z.boolean(),
    expectedCustomMessage: z.string().nullish(),
  }),
  z.object({
    kind: z.literal("SetCanEdit"),
    reviewerUserId: z.uuid(),
    reviewerLegalEntityId: z.uuid(),
    expectedCanEdit: z.boolean(),
    canEdit: z.boolean(),
  }),
  z.object({
    kind: z.literal("SetCustomMessage"),
    reviewerUserId: z.uuid(),
    reviewerLegalEntityId: z.uuid(),
    expectedCustomMessage: z.string().nullish(),
    customMessage: z.string().nullish(),
  }),
]);
export type ProposalReviewerChange = z.infer<typeof ProposalReviewerChangeZ>;

export const ProposalCollaborationCapabilitiesZ = z.object({
  canEditDraft: z.boolean(),
  canManageReviewers: z.boolean(),
  canSubmit: z.boolean(),
  canReview: z.boolean(),
  // Optional during the rolling API deployment; missing values are treated as
  // false by the UI, so an older envelope can never enable a terminal action.
  canReject: z.boolean().optional(),
  canCancel: z.boolean().optional(),
  canHire: z.boolean().optional(),
  canManageClientTeam: z.boolean().optional(),
  // Initial proposal setup covers negotiability, the Details Client Representative setup, and
  // selection of the negotiable Vendor Team. Client Team membership is reference state and uses
  // `canManageClientTeam`, never this content capability.
  canConfigureInitialProposalSetup: z.boolean().optional(),
  // Deprecated rolling-deployment alias. Keep parsing it until every supported API deployment
  // supplies `canConfigureInitialProposalSetup`.
  canSelectInitialTeams: z.boolean().optional(),
});
export type ProposalCollaborationCapabilities = z.infer<typeof ProposalCollaborationCapabilitiesZ>;

export const ProposalCollaborationEnvelopeZ = z.object({
  negotiationId: z.uuid(),
  streamVersion: z.number().int().nonnegative(),
  contentVersion: z.number().int().nonnegative(),
  currentSubmissionId: z.uuid(),
  viewingParty: ProposalNegotiationPartyZ,
  projection: ProposalSchema.ProposalJSONZ,
  // Proposal-authorized participant display metadata. Optional while API deployments roll forward;
  // older envelopes fall back to the existing Marketplace lookup in the Details tab.
  clientRepresentativeName: z.string().min(1).nullish(),
  capabilities: ProposalCollaborationCapabilitiesZ,
  acknowledgedCommandIds: z.array(z.uuid()).default([]),
  conflicts: z.array(ProposalConflictZ).default([]),
  // Only ever non-empty on the envelope returned from a save; every other command kind (and any
  // older API deploy) parses as "everything applied" -- see ProposalOperationReceiptZ.
  operationReceipts: z.array(ProposalOperationReceiptZ).default([]),
  changeLines: z.array(ProposalChangeLineZ).default([]),
  resolutionStates: z.array(ProposalResolutionStateZ),
  reviewerApprovals: z.array(ProposalReviewerApprovalZ).default([]),
  recentActivity: z.array(ProposalActivityZ).default([]),
  // Per-path last-seen negotiation stream version for the viewer; the whole-proposal cursor is under
  // the "" key. Defaulted so an older API payload (or a rolling deploy) parses without the field.
  viewerSeenThroughStreamVersion: z.record(z.string(), z.number().int().nonnegative()).default({}),
  // The whole-proposal ("") cursor surfaced directly. 0 when the viewer has never marked anything seen.
  viewerSeenAllThroughStreamVersion: z.number().int().nonnegative().default(0),
  // The counterparty's first-submission sequence: content authored at or before it is the viewer's own
  // baseline (what they started from), not incoming counterparty activity, and the server already
  // excludes it from being surfaced as unread. Null before the counterparty has ever submitted. Optional
  // so an older API payload (or a rolling deploy) parses without the field — the unread/collision
  // predicates treat a missing value as "no floor" (0), matching prior behavior.
  baselineSequence: z.number().int().nonnegative().nullable().optional(),
  // Set when this negotiation opened to revise an already-hired work agreement, and then equal to the
  // last sequence of the agreed terms it opened from. A change line at or before it records a term
  // both parties had already signed rather than a change either of them published during the
  // negotiation, and the field's mere presence is how the submit dialog knows it is describing changes
  // to a live agreement. Null/absent for a first-time offer, and for an older API payload — which is
  // exactly the pre-existing behaviour.
  agreedBaselineSequence: z.number().int().nonnegative().nullable().optional(),
});
// The two viewer-seen cursors and operationReceipts are re-declared optional so the many
// hand-built envelope literals across the suite need not carry them; the schema still fills
// concrete defaults for every parsed API payload.
export type ProposalCollaborationEnvelope = Omit<
  z.infer<typeof ProposalCollaborationEnvelopeZ>,
  "projection" | "viewerSeenThroughStreamVersion" | "viewerSeenAllThroughStreamVersion" | "operationReceipts"
> & {
  projection: ProposalJSON;
  viewerSeenThroughStreamVersion?: Record<string, number>;
  viewerSeenAllThroughStreamVersion?: number;
  operationReceipts?: ProposalOperationReceipt[];
};

export const ProposalTimelineEntryZ = z
  .object({
    sequence: z.number().int().positive(),
    commandId: z.uuid().optional(),
    kind: ProposalNegotiationEventKindZ,
    sourceParty: ProposalNegotiationPartyZ.nullish(),
    affectedParty: ProposalNegotiationPartyZ.nullish(),
    path: z.string().startsWith("/").nullish(),
    itemId: z.string().min(1).nullish(),
    conflictKey: z.string().min(1).nullish(),
    messageId: z.uuid().nullish(),
    commentAudience: ProposalCommentAudienceZ.nullish(),
    expectedValueExists: z.boolean(),
    expectedValue: z.json().optional(),
    valueExists: z.boolean(),
    value: z.json().optional(),
    actorUserId: z.uuid().nullish(),
    actorLegalEntityId: z.uuid().nullish(),
    // Who authored the entry. System is also used by later automatic events, so it must not double as
    // the creation-baseline discriminator.
    actorRole: ProposalActorRoleZ.nullish(),
    // Explicitly distinguishes the proposal's initial values from later System-authored events. Optional
    // for a rolling deploy; the presenter retains the legacy actor-role fallback only when this is absent.
    isCreationBaseline: z.boolean().optional(),

    createdDate: z.iso.datetime({ offset: true }),
  })
  .superRefine((entry, context) => {
    const hasExpectedValue = Object.prototype.hasOwnProperty.call(entry, "expectedValue");
    if (entry.expectedValueExists !== hasExpectedValue) {
      context.addIssue({
        code: "custom",
        path: ["expectedValue"],
        message: "expectedValue must be present exactly when expectedValueExists is true.",
      });
    }
    const hasValue = Object.prototype.hasOwnProperty.call(entry, "value");
    if (entry.valueExists !== hasValue) {
      context.addIssue({
        code: "custom",
        path: ["value"],
        message: "value must be present exactly when valueExists is true.",
      });
    }
    if (entry.kind === "CommentThreadLinked") {
      if (!entry.messageId) {
        context.addIssue({ code: "custom", path: ["messageId"], message: "A linked comment requires messageId." });
      }
      if (!entry.commentAudience) {
        context.addIssue({
          code: "custom",
          path: ["commentAudience"],
          message: "A linked comment requires an audience.",
        });
      }
      if (entry.expectedValueExists || entry.valueExists) {
        context.addIssue({
          code: "custom",
          path: ["value"],
          message: "Work timeline comment links cannot contain Messaging comment bodies.",
        });
      }
    }
  });
export type ProposalTimelineEntry = z.infer<typeof ProposalTimelineEntryZ>;

export const ProposalTimelineResponseZ = z.object({
  negotiationId: z.uuid(),
  streamVersion: z.number().int().nonnegative(),
  viewingParty: ProposalNegotiationPartyZ,
  path: z.string().startsWith("/").nullish(),
  itemId: z.string().min(1).nullish(),
  entries: z.array(ProposalTimelineEntryZ),
});
export type ProposalTimelineResponse = z.infer<typeof ProposalTimelineResponseZ>;

export const SaveProposalDraftCommandZ = z
  .object({
    commandId: z.uuid(),
    expectedSubmissionId: z.uuid(),
    expectedStreamVersion: z.number().int().nonnegative(),
    expectedContentVersion: z.number().int().nonnegative(),
    operations: z.array(ProposalContentOperationZ),
    reviewerChanges: z.array(ProposalReviewerChangeZ),
  })
  .refine((command) => command.operations.length > 0 || command.reviewerChanges.length > 0, {
    message: "A proposal save command must contain a content or reviewer change.",
  });
export type SaveProposalDraftCommand = z.infer<typeof SaveProposalDraftCommandZ>;

export const ReplaceClientTeamMembersCommandZ = z.object({
  commandId: z.uuid(),
  expectedStreamVersion: z.number().int().nonnegative(),
  members: z.array(
    z.object({
      userId: z.uuid(),
      legalEntityId: z.uuid(),
    }),
  ),
});
export type ReplaceClientTeamMembersCommand = z.infer<typeof ReplaceClientTeamMembersCommandZ>;

/**
 * One selected change in a submit tranche, addressed the way resolution targets are already
 * keyed — conflict key, plus item ID for a collection entry. Never an array index: the browser's
 * candidate list and the server's are computed independently.
 */
export const ProposalPublicationSelectionZ = z.object({
  conflictKey: z.string().min(1),
  itemId: z.string().min(1).nullish(),
});
export type ProposalPublicationSelection = z.infer<typeof ProposalPublicationSelectionZ>;

export const SubmitProposalDraftCommandZ = z.object({
  commandId: z.uuid(),
  expectedSubmissionId: z.uuid(),
  expectedStreamVersion: z.number().int().nonnegative(),
  expectedContentVersion: z.number().int().nonnegative(),
});
export type SubmitProposalDraftCommand = z.infer<typeof SubmitProposalDraftCommandZ>;

export const RequestProposalRevisionNegotiationCommandZ = SubmitProposalDraftCommandZ;
export type RequestProposalRevisionNegotiationCommand = z.infer<
  typeof RequestProposalRevisionNegotiationCommandZ
>;

export const CompleteProposalReviewCommandZ = z.object({
  commandId: z.uuid(),
  expectedSubmissionId: z.uuid(),
  expectedStreamVersion: z.number().int().nonnegative(),
  expectedContentVersion: z.number().int().nonnegative(),
});
export type CompleteProposalReviewCommand = z.infer<typeof CompleteProposalReviewCommandZ>;
/**
 * A team leader asking one named reviewer for a fresh review. Same cursor contract as
 * `CompleteProposalReviewCommandZ`, plus the reviewer being asked — here the reviewer is the
 * subject rather than the caller.
 */
export const RequestProposalReviewCommandZ = z.object({
  commandId: z.uuid(),
  expectedSubmissionId: z.uuid(),
  expectedStreamVersion: z.number().int().nonnegative(),
  expectedContentVersion: z.number().int().nonnegative(),
  reviewerUserId: z.uuid(),
  reviewerLegalEntityId: z.uuid(),
});
export type RequestProposalReviewCommand = z.infer<typeof RequestProposalReviewCommandZ>;


export const ApproveProposalNegotiationCommandZ = z.object({
  commandId: z.uuid(),
  expectedSubmissionId: z.uuid(),
  expectedStreamVersion: z.number().int().nonnegative(),
  expectedContentVersion: z.number().int().nonnegative(),
});
export type ApproveProposalNegotiationCommand = z.infer<typeof ApproveProposalNegotiationCommandZ>;

export const RejectProposalNegotiationCommandZ = ApproveProposalNegotiationCommandZ;
export type RejectProposalNegotiationCommand = z.infer<typeof RejectProposalNegotiationCommandZ>;

export const CancelProposalNegotiationCommandZ = ApproveProposalNegotiationCommandZ;
export type CancelProposalNegotiationCommand = z.infer<typeof CancelProposalNegotiationCommandZ>;

export const HireProposalNegotiationCommandZ = ApproveProposalNegotiationCommandZ;
export type HireProposalNegotiationCommand = z.infer<typeof HireProposalNegotiationCommandZ>;

export const ProposalConflictResponseZ = z.object({
  negotiationId: z.uuid(),
  streamVersion: z.number().int().nonnegative(),
  contentVersion: z.number().int().nonnegative(),
  // A stale base/cursor is a structured 409 even when the merge found no
  // path-level conflicts. The returned envelope is still required so the
  // client can rebase and retry against its new submission/version cursors.
  conflicts: z.array(ProposalConflictZ),
  envelope: ProposalCollaborationEnvelopeZ,
});
export type ProposalConflictResponse = z.infer<typeof ProposalConflictResponseZ>;

export const ProposalCollaborationRealtimeNoticeZ = z
  .object({
    negotiationId: z.uuid(),
    proposalId: z.uuid(),
    streamVersion: z.number().int().nonnegative(),
    commandId: z.uuid(),
  })
  .strict();
export type ProposalCollaborationRealtimeNotice = z.infer<typeof ProposalCollaborationRealtimeNoticeZ>;
